// One racing cat: places its model from the race snapshot and decides what it is doing.
//
// The model stands where `displayPos` says, in its own lane, drifting a little within it. A cat
// that has finished runs on past the line, slows and settles. While the signal says it is eating,
// it eats with the bacon in its mouth. While it is tripping, the catnip trip job can take over its
// placement and pose through `override`; until then a plain float over the catch spot stands in.
import { AnimationMixer, Group, MathUtils, Quaternion, Vector3 } from 'three';
import type { CatDef } from '../../data/cats';
import { CAT_DISPLAY_SCALE } from '../../data/cats';
import { laneCenterZ, posToX, TRACK_LENGTH } from '../../data/track';
import type { CatSnapshot, Phase } from '../../race/types';
import { CatAnimator, type CatMode, type SettleClip } from './animator';
import { boneName, type BaseCat, CatModel } from './catFactory';
import { BEHAVIOUR_POSE, PodiumIdle, type PodiumBehaviour, type PodiumPlace, POSE_LAYERS, type PoseLayer } from './podiumIdle';
import { CatSecondary, LIE_DROP } from './secondary';

/** How far behind the start line (m) a cat stands in its stall at post time, so its head stays
 *  behind the closed doors (front at x ≈ 0.04) instead of poking through them. */
export const START_SETBACK = 0.8;
/** Over this many metres of running the setback fades out, so the cat runs out of its stall. */
export const START_SETBACK_FADE = 2.5;

/** World x of a racing cat at race position `pos`: the track position, pulled back into the stall near the start. */
export function startX(pos: number): number {
  const x = posToX(pos);
  return x - START_SETBACK * Math.max(0, 1 - Math.max(0, x) / START_SETBACK_FADE);
}

/**
 * Placement and pose supplied by the catnip trip while it owns a cat.
 * `position` is where the cat's paws would be (world); `heading` is radians from +X towards +Z.
 */
export interface CatOverride {
  position: Vector3;
  heading: number;
  /** Extra body rotation (radians) about the cat's own axes: roll (forward axis), pitch, spin (up). */
  roll?: number;
  pitch?: number;
  spin?: number;
  mode: Extract<CatMode, 'float' | 'land' | 'run'>;
  /** 0..1, the blank glowing trip eyes. */
  tripEyes: number;
  /** m/s, drives the gait if mode is 'run'. */
  speed?: number;
}

export interface ActorFrame {
  phase: Phase;
  dt: number;
  timeS: number;
  /** This cat won and the results are showing. */
  winner: boolean;
  /** World point to look at when settled (usually the camera), or null. */
  look: Vector3 | null;
  /** Where this cat sits for the results, once they are in; null or absent keeps it on the track. */
  podium?: PodiumPlace | null;
}

/** Seconds the winner celebrates on the top block before it settles like the others. */
export const PODIUM_CELEBRATE_S = 4;

const RUNOUT_DECEL = 1.7; // m/s² after the finish line
const FLOAT_HEIGHT = 1.1;

const _q = new Quaternion();

export class CatActor {
  /** World placement. */
  readonly group = new Group();
  /** Lean into speed changes and drift, about the paws. */
  private lean = new Group();
  readonly model: CatModel;
  readonly animator: CatAnimator;
  private secondary: CatSecondary;
  /** Set by the catnip trip while it owns this cat; null hands the cat back. */
  override: CatOverride | null = null;

  private x = 0;
  private z = 0;
  private heading = 0;
  private speed = 0;
  private prevSpeed = 0;
  private accel = 0;
  private yawRate = 0;
  private wobbleT: number;
  private finishedAt: number | null = null;
  private runout = 0;
  private runoutSpeed = 0;
  private settle: SettleClip = 'idle';
  private settleFor = 0;
  private wasTripping = false;
  private landing = false;
  private tripEyes = 0;
  private catchX = 0;
  private mode: CatMode = 'settle';
  private first = true;
  private rnd: () => number;
  /** Podium life: the behaviour picker, what the cat is doing, for how much longer, and layer weights. */
  private podiumIdle: PodiumIdle;
  private onPodiumFor = -1;
  private behaviour: PodiumBehaviour | null = null;
  private behaviourFor = 0;
  private layerW: Record<PoseLayer, number> = { lie: 0, stretch: 0, face: 0, flick: 0, lookAround: 0 };

  constructor(readonly def: CatDef, model: CatModel, base: BaseCat) {
    this.model = model;
    this.group.name = `cat-${def.id}`;
    this.group.add(this.lean);
    this.lean.add(model.root);
    let s = def.lane * 7919 + 17;
    this.rnd = () => { s = (s * 16807) % 2147483647; return s / 2147483647; };
    this.wobbleT = this.rnd() * 100;
    this.mixer = new AnimationMixer(model.rig);
    this.animator = new CatAnimator(this.mixer, base.clips, base.info, def.build.legLength * def.build.size * CAT_DISPLAY_SCALE, def.personality.energy, def.lane + 1);
    this.secondary = new CatSecondary(model.rig, model.bones, def.personality, def.lane + 3);
    this.podiumIdle = new PodiumIdle(def.personality, def.lane * 7349 + 101);
  }

  readonly mixer: AnimationMixer;

  get currentMode(): CatMode { return this.mode; }
  /** What the cat is doing on the podium ('celebrate' while the winner celebrates), or null when it is not there. */
  get podiumBehaviour(): PodiumBehaviour | 'celebrate' | null {
    if (this.onPodiumFor < 0) return null;
    return this.mode === 'celebrate' ? 'celebrate' : this.behaviour;
  }

  /** A point just above the cat's head, for name tags and cameras. */
  tagAnchor(out = new Vector3()): Vector3 {
    const head = this.model.bones.get(boneName('spine.006'));
    if (head) head.getWorldPosition(out); else this.group.getWorldPosition(out);
    return out.setY(out.y + 0.16 * CAT_DISPLAY_SCALE);
  }

  /** The cat's body centre in world space, for camera targets. */
  bodyCentre(out = new Vector3()): Vector3 {
    return this.group.localToWorld(out.set(0, 0.2 * this.def.build.size * CAT_DISPLAY_SCALE, 0));
  }

  update(c: CatSnapshot, f: ActorFrame): void {
    const dt = f.dt;
    const p = this.def.personality;

    // ---- trip handover: the override wins; otherwise a stand-in float and a landing stagger
    if (c.tripping && !this.wasTripping) this.catchX = this.x;
    // the trip plays its own landing while it owns the cat; the stand-in stagger is only for no trip
    if (!c.tripping && this.wasTripping && !this.override) this.landing = true;
    this.wasTripping = c.tripping;

    let mode: CatMode;
    let targetSpeed = 0;
    let lift = 0;
    let spin = 0;
    const o = this.override;
    if (o) {
      mode = o.mode;
      targetSpeed = o.speed ?? 0;
    } else if (c.tripping) {
      mode = 'float';
      lift = FLOAT_HEIGHT;
      spin = f.timeS * 0.6;
    } else if (this.landing) {
      mode = 'land';
      if (this.animator.currentMode === 'land' && this.animator.landingDone) { this.landing = false; mode = 'run'; }
    } else if (f.phase === 'starting') {
      mode = 'settle';
      this.settle = p.energy < 0.5 ? 'sit' : 'idle';
    } else if (c.eating) {
      mode = 'eat';
    } else if (f.podium) {
      mode = f.winner && this.onPodiumFor < PODIUM_CELEBRATE_S ? 'celebrate' : 'settle';
    } else if (c.finished) {
      mode = f.winner ? 'celebrate' : this.runoutSpeed > 0.25 ? 'run' : 'settle';
    } else {
      mode = 'run';
      targetSpeed = c.speed;
    }

    // ---- where along the track
    if (!c.finished) { this.finishedAt = null; this.runout = 0; }
    if (c.finished && this.finishedAt === null) {
      this.finishedAt = f.timeS;
      this.runoutSpeed = Math.max(this.speed, 2.5);
      this.runout = 0;
    }
    let x: number;
    if (c.tripping && !o) x = this.catchX;
    else if (c.finished) {
      this.runoutSpeed = Math.max(0, this.runoutSpeed - RUNOUT_DECEL * dt);
      this.runout += this.runoutSpeed * dt;
      x = TRACK_LENGTH + this.runout;
      if (mode === 'run') targetSpeed = this.runoutSpeed;
    } else x = startX(c.displayPos);

    // ---- drift within the lane while running
    if (mode === 'run') this.wobbleT += dt * (0.5 + Math.min(1, this.speed / 4));
    const w = p.wobble * (0.6 * Math.sin(this.wobbleT * 0.23 + this.def.lane) + 0.4 * Math.sin(this.wobbleT * 0.51 + this.def.lane * 2.1));
    const z = laneCenterZ(c.lane) + MathUtils.clamp(w, -0.3, 0.3);

    // ---- speed for the gait: smoothed so signal noise does not twitch the legs
    if (this.first) { this.x = x; this.z = z; this.speed = targetSpeed; }
    const ks = 1 - Math.exp(-dt / 0.25);
    this.speed += (targetSpeed - this.speed) * ks;
    this.accel = dt > 0 ? MathUtils.lerp(this.accel, (this.speed - this.prevSpeed) / dt, 1 - Math.exp(-dt / 0.3)) : 0;
    this.prevSpeed = this.speed;

    const dx = x - this.x, dz = z - this.z;
    let heading = this.heading;
    if (dt > 0 && Math.hypot(dx, dz) > 0.002 && dx > -0.5) heading = Math.atan2(dz, Math.max(0.05, dx));
    heading = MathUtils.clamp(heading, -0.5, 0.5);
    const kh = 1 - Math.exp(-dt / 0.2);
    const newHeading = this.heading + (heading - this.heading) * kh;
    this.yawRate = dt > 0 ? (newHeading - this.heading) / dt : 0;
    this.heading = newHeading;
    this.x = x; this.z = z;

    // ---- on the podium: sit on the spot, facing the grandstand, and do cat things
    const pod = !o && !c.tripping ? f.podium ?? null : null;
    if (pod) {
      if (this.onPodiumFor < 0) {
        this.onPodiumFor = 0;
        const s = this.podiumIdle.first();
        this.behaviour = s.behaviour; this.behaviourFor = s.hold;
        this.heading = pod.heading; this.yawRate = 0; this.speed = 0; this.accel = 0;
        this.lean.rotation.set(0, 0, 0);
      }
      this.onPodiumFor += dt;
      if (mode === 'settle') {
        this.behaviourFor -= dt;
        if (this.behaviourFor <= 0) {
          const s = this.podiumIdle.next();
          this.behaviour = s.behaviour; this.behaviourFor = s.hold;
        }
        this.settle = BEHAVIOUR_POSE[this.behaviour ?? 'sit'].clip;
      }
      this.x = pod.x; this.z = pod.z; this.heading = pod.heading;
    } else this.onPodiumFor = -1;
    const layer = pod && mode === 'settle' ? BEHAVIOUR_POSE[this.behaviour ?? 'sit'].layer : null;
    const kl = 1 - Math.exp(-dt * 1.6);
    for (const name of POSE_LAYERS) this.layerW[name] += ((name === layer ? 1 : 0) - this.layerW[name]) * kl;

    // ---- settling after the finish: change between sit, groom and idle now and then
    if (mode === 'settle' && f.phase !== 'starting' && !pod) {
      this.settleFor -= dt;
      if (this.settleFor <= 0) {
        const s = p.settle, total = s.sit + s.groom + s.idle;
        const r = this.rnd() * total;
        this.settle = r < s.sit ? 'sit' : r < s.sit + s.groom ? 'groom' : 'idle';
        this.settleFor = 7 + this.rnd() * 8;
      }
    }

    // ---- place the cat
    if (o) {
      this.group.position.copy(o.position);
      this.group.rotation.set(0, Math.PI / 2 - o.heading, 0);
      this.lean.rotation.set(o.pitch ?? 0, o.spin ?? 0, o.roll ?? 0);
    } else if (pod) {
      this.group.position.set(pod.x, pod.y, pod.z);
      this.group.rotation.set(0, Math.PI / 2 - pod.heading, 0);
      this.lean.rotation.set(0, 0, 0);
      this.lean.position.y = -LIE_DROP * this.def.build.size * CAT_DISPLAY_SCALE * this.layerW.lie;
    } else {
      this.lean.position.y = 0;
      this.group.position.set(this.x, lift, this.z);
      this.group.rotation.set(0, Math.PI / 2 - this.heading + spin, 0);
      const roll = MathUtils.clamp(-this.yawRate * this.speed * 0.06, -0.12, 0.12);
      const pitch = MathUtils.clamp(-this.accel * 0.012, -0.06, 0.06);
      this.lean.rotation.x += (pitch - this.lean.rotation.x) * (1 - Math.exp(-dt / 0.2));
      this.lean.rotation.z += (roll - this.lean.rotation.z) * (1 - Math.exp(-dt / 0.2));
      this.lean.rotation.y = 0;
    }

    // ---- animate
    this.mode = mode;
    this.model.mouthBacon.visible = mode === 'eat';
    const tripTarget = o ? o.tripEyes : c.tripping ? 1 : 0;
    this.tripEyes += (tripTarget - this.tripEyes) * (1 - Math.exp(-dt / 0.35));
    this.model.setTrip(this.tripEyes, f.timeS);

    const input = { mode, speed: this.speed, settle: this.settle };
    // the mixer only writes changed values, so undo last frame's additions before it runs
    this.secondary.restore();
    this.model.removeBuild();
    if (this.first) { this.animator.snap(input, this.rnd() * 2); this.first = false; } else this.animator.update(dt, input);
    this.model.applyBuild();
    this.group.updateMatrixWorld(true);
    this.group.getWorldQuaternion(_q);
    // look ahead while running, at the camera when settled
    const look = mode === 'run' ? null : f.look;
    this.secondary.update(dt, f.timeS, { mode, speed: this.speed, accel: this.accel, yawRate: this.yawRate, look, catQ: _q, layers: pod ? this.layerW : undefined });
  }
}
